import { useState } from "react";
import { Store, Plus, Check } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Card, PageHeader, StatusBadge, InlineNotice } from "../components/ui";

export function StoreBranchesPage() {
  const auth = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [storeName, setStoreName] = useState("");
  const [businessType, setBusinessType] = useState("Tạp hóa");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const currentStoreId = auth.session?.store.id;
  const stores = auth.session?.stores || (auth.session?.store ? [auth.session.store] : []);

  async function handleSwitch(storeId: string) {
    if (storeId === currentStoreId) return;
    setBusy(true);
    setError("");
    try {
      await auth.switchStore(storeId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể chuyển cửa hàng");
    } finally {
      setBusy(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const created = await api<{ id: string }>("/stores", {
        method: "POST",
        body: JSON.stringify({
          name: storeName,
          businessType,
        }),
      });

      // Switch to newly created store
      await auth.switchStore(created.id);
      setShowAdd(false);
      setStoreName("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể tạo chi nhánh mới");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        title="Quản Lý Chi Nhánh"
        description="Chuyển đổi điểm bán hoặc thiết lập chi nhánh mới thuộc tổ chức"
      />

      {error ? <InlineNotice tone="danger" title={error} /> : null}

      <section>
        <div className="section-title">
          <div><h2>Danh Sách Chi Nhánh</h2></div>
          {auth.session?.user.role === "OWNER" ? (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowAdd(!showAdd)}
              disabled={busy}
            >
              <Plus size={16} /> Thêm chi nhánh
            </button>
          ) : null}
        </div>

        {showAdd ? (
          <Card className="form-card">
            <form onSubmit={handleCreate} className="form-stack">
              <label>
                Tên chi nhánh mới
                <input
                  placeholder="VD: Chi nhánh 2 - Quận 7"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  required
                />
              </label>
              <label>
                Loại hình
                <select value={businessType} onChange={(e) => setBusinessType(e.target.value)}>
                  <option>Tạp hóa</option>
                  <option>Thực phẩm</option>
                  <option>Mini mart</option>
                  <option>Bán lẻ</option>
                </select>
              </label>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button className="btn btn-primary" disabled={busy}>
                  {busy ? "Đang tạo..." : "Xác nhận tạo"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAdd(false)}
                >
                  Hủy
                </button>
              </div>
            </form>
          </Card>
        ) : null}

        <div className="menu-list">
          {stores.map((st) => {
            const isCurrent = st.id === currentStoreId;
            return (
              <div
                key={st.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.875rem 1rem",
                  background: "var(--card-bg, #fff)",
                  borderRadius: "8px",
                  border: isCurrent ? "2px solid #2563eb" : "1px solid var(--border-color, #e5e7eb)",
                  marginBottom: "0.5rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <Store size={22} color={isCurrent ? "#2563eb" : "#64748b"} />
                  <div>
                    <strong style={{ display: "block", fontSize: "1rem" }}>{st.name}</strong>
                    <small style={{ color: "var(--color-muted, #64748b)" }}>
                      {st.businessType || "Bán lẻ"} {st.role ? `· ${st.role}` : ""}
                    </small>
                  </div>
                </div>

                <div>
                  {isCurrent ? (
                    <StatusBadge tone="success">
                      <Check size={14} style={{ marginRight: 4 }} /> Đang chọn
                    </StatusBadge>
                  ) : (
                    <button
                      className="btn btn-secondary btn-sm"
                      disabled={busy}
                      onClick={() => handleSwitch(st.id)}
                    >
                      Chuyển sang
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
